/**
 * Historique d'usage des installations : quotas d'utilisation et bouchons.
 *
 * Le serveur mesure chaque installation par tranches de 15 minutes de jeu,
 * de 8 h à 17 h 30 : part du débit nominal utilisée, file et attente
 * maximales, clients servis et perdus, minutes hors service. Il garde le
 * détail des 7 derniers jours et un résumé par installation des 28 derniers,
 * plus un journal des faits marquants (pannes, livraisons, bouchons).
 *
 * L'historique voyage sous forme de patchs (nouvelle journée, tranche close,
 * résumé, journal) appliqués par la même fonction des deux côtés : la
 * réplique du gérant reste identique à l'état du serveur.
 */
import type { AlertLevel, EntityId } from './state';
import { formatHour } from './time';

export const USAGE_BUCKET_MINUTES = 15;
/** Début de la plage mesurée (arrivée des premiers visiteurs). */
export const USAGE_FIRST_MINUTE = 8 * 60;
/** Fin de la plage mesurée (domaine vidé). */
export const USAGE_LAST_MINUTE = 17 * 60 + 30;
export const USAGE_BUCKETS = (USAGE_LAST_MINUTE - USAGE_FIRST_MINUTE) / USAGE_BUCKET_MINUTES;
/** Jours gardés en détail (tranches de 15 min), aujourd'hui compris. */
export const USAGE_DETAIL_DAYS = 7;
/** Jours gardés en résumé. */
export const USAGE_SUMMARY_DAYS = 28;
export const JOURNAL_MAX = 120;

export type FacilityKind = 'accueil' | 'ticket' | 'rental' | 'food' | 'lift' | 'piste' | 'stock';

/**
 * Métriques d'une tranche :
 * - u : utilisation en % (débit nominal des comptoirs et remontées, places des
 *   restaurants, confort des pistes, capacité d'accueil) ;
 * - q : file maximale (personnes) ; w : attente maximale (s de simulation) ;
 * - n : servis (forfaits, paires louées, repas, embarquements, descentes, entrées) ;
 * - x : clients perdus ; off : minutes hors service ;
 * - crowd : visiteurs présents (max) ; sat : satisfaction moyenne ;
 * - food, fuel, water : niveau des stocks en % de la capacité.
 */
export type UsageMetric = 'u' | 'q' | 'w' | 'n' | 'x' | 'off' | 'crowd' | 'sat' | 'food' | 'fuel' | 'water';

/** Métriques enregistrées par type d'installation, dans l'ordre des lignes de patch. */
export const FACILITY_METRICS: Record<FacilityKind, readonly UsageMetric[]> = {
  accueil: ['u', 'n', 'x', 'crowd', 'sat'],
  ticket: ['u', 'q', 'w', 'n', 'x', 'off'],
  rental: ['u', 'q', 'w', 'n', 'x', 'off'],
  food: ['u', 'n', 'x', 'off'],
  lift: ['u', 'q', 'w', 'n', 'off'],
  piste: ['u', 'n', 'off'],
  stock: ['food', 'fuel', 'water'],
};

export const FACILITY_GROUP: Record<FacilityKind, { label: string; order: number }> = {
  accueil: { label: 'Accueil', order: 0 },
  ticket: { label: 'Caisses', order: 1 },
  rental: { label: 'Location', order: 2 },
  food: { label: 'Restauration', order: 3 },
  lift: { label: 'Remontées', order: 4 },
  piste: { label: 'Pistes', order: 5 },
  stock: { label: 'Stocks', order: 6 },
};

export interface FacilityRef {
  kind: 'lift' | 'piste' | 'building';
  id: EntityId;
}

export interface FacilityMeta {
  /** « accueil », « stocks », « L12 », « P8 », « B5:ticket », « B5:food »... */
  key: string;
  kind: FacilityKind;
  label: string;
  ref: FacilityRef | null;
  /**
   * Capacité au moment de la mesure : pers./h (remontée), clients/h
   * (comptoir), places (restaurant), skieurs (piste), visiteurs (accueil).
   */
  cap: number;
  /** Location : paires de skis du parc. */
  fleet?: number;
}

export type FacilitySeries = Partial<Record<UsageMetric, number[]>>;

export interface UsageDay {
  day: number;
  /** Tranches closes. */
  n: number;
  facilities: FacilityMeta[];
  /** Clé d'installation -> métrique -> une valeur par tranche (-1 : pas de mesure). */
  series: Record<string, FacilitySeries>;
}

export interface FacilitySummary {
  key: string;
  kind: FacilityKind;
  label: string;
  ref: FacilityRef | null;
  /** Utilisation moyenne et maximale (%), tranches hors service exclues de la moyenne. */
  avgU: number;
  peakU: number;
  maxW: number;
  maxQ: number;
  served: number;
  lost: number;
  /** Minutes passées saturé ou en bouchon. */
  jamMin: number;
  /** Niveau le plus haut atteint (-1 fermé toute la journée, 0 fluide ... 3 bouchon). */
  level: number;
  /** Pire épisode : tranches de début et de fin (incluses), ou null. */
  worst: [number, number] | null;
}

export interface DaySummary {
  day: number;
  facilities: FacilitySummary[];
}

export interface JournalEntry {
  id: number;
  /** Horloge de jeu. */
  t: number;
  level: AlertLevel;
  title: string;
  text?: string;
  ref?: FacilityRef;
}

export interface UsageHistory {
  /** Détail par tranche, du plus ancien au jour en cours. */
  days: UsageDay[];
  /** Résumés des journées terminées, du plus ancien au plus récent. */
  summaries: DaySummary[];
  /** Faits marquants, du plus ancien au plus récent. */
  journal: JournalEntry[];
}

export interface UsagePatch {
  /** Début d'une journée (détail vide). */
  newDay?: number;
  /** Installations apparues ou modifiées dans la journée en cours. */
  meta?: FacilityMeta[];
  /** Tranche close de la journée en cours : clé -> valeurs dans l'ordre de FACILITY_METRICS. */
  bucket?: { index: number; rows: Record<string, number[]> };
  /** Résumé de la journée qui s'achève. */
  summary?: DaySummary;
  journal?: JournalEntry[];
}

export function emptyUsageHistory(day: number): UsageHistory {
  return { days: [{ day, n: 0, facilities: [], series: {} }], summaries: [], journal: [] };
}

/** Applique un patch (serveur et réplique : même code, même résultat). */
export function applyUsagePatch(h: UsageHistory, p: UsagePatch): void {
  if (p.newDay !== undefined) {
    h.days.push({ day: p.newDay, n: 0, facilities: [], series: {} });
    while (h.days.length > USAGE_DETAIL_DAYS) h.days.shift();
  }
  const today = h.days[h.days.length - 1];
  if (today && p.meta) {
    for (const m of p.meta) {
      const i = today.facilities.findIndex((f) => f.key === m.key);
      if (i >= 0) today.facilities[i] = m;
      else today.facilities.push(m);
    }
  }
  if (today && p.bucket) {
    const { index, rows } = p.bucket;
    for (const [key, values] of Object.entries(rows)) {
      const meta = today.facilities.find((f) => f.key === key);
      if (!meta) continue;
      const series = (today.series[key] ??= {});
      FACILITY_METRICS[meta.kind].forEach((metric, j) => {
        const arr = (series[metric] ??= []);
        while (arr.length < index) arr.push(-1);
        arr[index] = values[j] ?? -1;
      });
    }
    today.n = Math.max(today.n, index + 1);
  }
  if (p.summary) {
    h.summaries.push(p.summary);
    while (h.summaries.length > USAGE_SUMMARY_DAYS) h.summaries.shift();
  }
  if (p.journal) {
    h.journal.push(...p.journal);
    if (h.journal.length > JOURNAL_MAX) h.journal.splice(0, h.journal.length - JOURNAL_MAX);
  }
}

/* ------------------------------------------------------------------ */
/* Tranches                                                             */
/* ------------------------------------------------------------------ */

/** Tranche (0..USAGE_BUCKETS-1) d'une minute de la journée, ou -1 hors plage. */
export function usageBucketAt(minute: number): number {
  if (minute < USAGE_FIRST_MINUTE || minute >= USAGE_LAST_MINUTE) return -1;
  return Math.floor((minute - USAGE_FIRST_MINUTE) / USAGE_BUCKET_MINUTES);
}

export function bucketStart(index: number): number {
  return USAGE_FIRST_MINUTE + index * USAGE_BUCKET_MINUTES;
}

/** « 9 h 15 » */
export function bucketLabel(index: number): string {
  return formatHour(bucketStart(index));
}

/** « 9 h 15 à 10 h 30 » (fin de la dernière tranche incluse). */
export function bucketRange(first: number, last = first): string {
  return `${formatHour(bucketStart(first))} à ${formatHour(bucketStart(last) + USAGE_BUCKET_MINUTES)}`;
}

/* ------------------------------------------------------------------ */
/* Niveaux de congestion                                                */
/* ------------------------------------------------------------------ */

export const LEVEL_LABEL = ['Fluide', 'Chargé', 'Saturé', 'Bouchon'] as const;
/** Installation hors service pendant la tranche. */
export const CLOSED = -1;

export interface UsageSample {
  /** Utilisation (1 = 100 %). */
  u?: number;
  /** Attente (s de simulation). */
  w?: number;
  /** Clients perdus. */
  x?: number;
  /** Minutes hors service dans la tranche. */
  off?: number;
}

/**
 * Niveau de congestion : 0 fluide, 1 chargé, 2 saturé, 3 bouchon, CLOSED si
 * l'installation est restée surtout hors service. Les seuils suivent le
 * ressenti du visiteur : quelques minutes de file à une remontée sont
 * normales, une file aux caisses ou à la location l'est beaucoup moins.
 */
export function congestionLevel(kind: FacilityKind, s: UsageSample): number {
  const u = s.u ?? 0;
  const w = s.w ?? 0;
  const x = s.x ?? 0;
  if ((s.off ?? 0) >= USAGE_BUCKET_MINUTES / 2 && u < 0.05 && x === 0 && w < 20) return CLOSED;
  switch (kind) {
    case 'lift':
      return w >= 300 ? 3 : w >= 150 ? 2 : w >= 60 || u >= 0.8 ? 1 : 0;
    case 'ticket':
    case 'rental':
      return x >= 3 || w >= 120 ? 3 : x > 0 || w >= 60 || u >= 0.95 ? 2 : w >= 20 || u >= 0.75 ? 1 : 0;
    case 'food':
      return x >= 3 ? 3 : x > 0 || u >= 0.95 ? 2 : u >= 0.75 ? 1 : 0;
    case 'accueil':
      return x >= 5 ? 3 : x > 0 || u >= 0.95 ? 2 : u >= 0.8 ? 1 : 0;
    case 'piste':
      return u >= 1.3 ? 3 : u >= 1 ? 2 : u >= 0.75 ? 1 : 0;
    default:
      return 0;
  }
}

/** Valeur d'une métrique à la tranche i, ou -1 sans mesure. */
export function seriesValue(s: FacilitySeries | undefined, metric: UsageMetric, i: number): number {
  const v = s?.[metric]?.[i];
  return v === undefined ? -1 : v;
}

/** Niveau d'une tranche enregistrée, ou null sans mesure. */
export function bucketLevel(kind: FacilityKind, s: FacilitySeries | undefined, i: number): number | null {
  const u = seriesValue(s, kind === 'stock' ? 'food' : 'u', i);
  if (u < 0) return null;
  if (kind === 'stock') return 0;
  const pos = (m: UsageMetric) => Math.max(0, seriesValue(s, m, i));
  return congestionLevel(kind, { u: u / 100, w: pos('w'), x: pos('x'), off: pos('off') });
}

export interface Jam {
  /** Tranches de début et de fin (incluses). */
  start: number;
  end: number;
  level: number;
  maxW: number;
  maxQ: number;
  lost: number;
  peakU: number;
}

/** Épisodes saturés ou en bouchon (tranches consécutives de niveau 2 ou plus), du plus grave au moins grave. */
export function findJams(kind: FacilityKind, s: FacilitySeries | undefined, n: number): Jam[] {
  const out: Jam[] = [];
  let cur: Jam | null = null;
  for (let i = 0; i < n; i++) {
    const lv = bucketLevel(kind, s, i);
    if (lv !== null && lv >= 2) {
      const w = Math.max(0, seriesValue(s, 'w', i));
      const q = Math.max(0, seriesValue(s, 'q', i));
      const x = Math.max(0, seriesValue(s, 'x', i));
      const u = Math.max(0, seriesValue(s, 'u', i));
      if (!cur) {
        cur = { start: i, end: i, level: lv, maxW: w, maxQ: q, lost: x, peakU: u };
        out.push(cur);
      } else {
        cur.end = i;
        cur.level = Math.max(cur.level, lv);
        cur.maxW = Math.max(cur.maxW, w);
        cur.maxQ = Math.max(cur.maxQ, q);
        cur.lost += x;
        cur.peakU = Math.max(cur.peakU, u);
      }
    } else {
      cur = null;
    }
  }
  return out.sort((a, b) => b.level - a.level || b.lost - a.lost || b.end - b.start - (a.end - a.start) || b.maxW - a.maxW);
}

export function summarizeFacility(meta: FacilityMeta, s: FacilitySeries | undefined, n: number): FacilitySummary {
  let uSum = 0;
  let uCnt = 0;
  let peakU = 0;
  let maxW = 0;
  let maxQ = 0;
  let served = 0;
  let lost = 0;
  let jamMin = 0;
  let level = CLOSED;
  for (let i = 0; i < n; i++) {
    const lv = bucketLevel(meta.kind, s, i);
    if (lv === null) continue;
    const u = Math.max(0, seriesValue(s, 'u', i));
    if (lv !== CLOSED) {
      uSum += u;
      uCnt++;
    }
    peakU = Math.max(peakU, u);
    maxW = Math.max(maxW, seriesValue(s, 'w', i));
    maxQ = Math.max(maxQ, seriesValue(s, 'q', i));
    served += Math.max(0, seriesValue(s, 'n', i));
    lost += Math.max(0, seriesValue(s, 'x', i));
    if (lv >= 2) jamMin += USAGE_BUCKET_MINUTES;
    level = Math.max(level, lv);
  }
  const worst = findJams(meta.kind, s, n)[0];
  return {
    key: meta.key,
    kind: meta.kind,
    label: meta.label,
    ref: meta.ref,
    avgU: uCnt ? Math.round(uSum / uCnt) : 0,
    peakU,
    maxW,
    maxQ,
    served,
    lost,
    jamMin,
    level,
    worst: worst ? [worst.start, worst.end] : null,
  };
}

/** Résumé d'une journée (stocks exclus). */
export function summarizeDay(d: UsageDay): DaySummary {
  return {
    day: d.day,
    facilities: d.facilities.filter((f) => f.kind !== 'stock').map((f) => summarizeFacility(f, d.series[f.key], d.n)),
  };
}
